// SAB-522: shared shell for the /terms, /privacy, /cookies legal pages.
//
// These routes previously 404'd (/terms, /cookies) or blank-redirected to Termly
// (/privacy). This shell makes them render real, in-app pages.
//
// IMPORTANT - legal content is a deliverable, NOT invented here:
//   - The authoritative policy copy is hosted on Termly (the same policyUUIDs
//     already used by MarketingLayout) and IS the published policy. We surface
//     it via an embedded viewer + a direct link, so the real existing content is
//     "wired in" without any legal text being written in this repo. (The earlier
//     "[PLACEHOLDER - LEGAL CONTENT NEEDED]" banner was removed: it falsely
//     implied the policy was missing when it renders directly below. Authoring
//     the copy natively in-repo instead of embedding Termly is an optional future
//     nicety, not a missing requirement.)
//
// SAB-767: the AeropaySection component and AEROPAY_LEGAL URLs were REMOVED. They
// existed to satisfy Aeropay Integration Agreement Section 6; Aeropay declined to
// onboard us and Soap replaced them, so the requirement no longer exists and the
// disclosure was actively wrong — these pages go to Soap's underwriting.
//
// If Soap's own agreement carries an equivalent embedded-ToS/Privacy requirement, add
// a SoapSection here on the same pattern. That is a question for counsel, not an
// assumption to carry forward: do NOT re-add a required section without the agreement
// text in hand. The removed one was never legal TEXT anyway — it was a "being
// finalized" notice plus a hyperlink.

import { ReactNode } from 'react';
import MarketingLayout from '@/components/shared/MarketingLayout';

// The canonical, currently-published policies (Termly-hosted). Mirrors the
// constants in MarketingLayout so both surfaces point at the same documents.
export const TERMLY_POLICIES = {
  terms: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=0eef0aaa-538d-4434-9471-2d372eb58c22',
  privacy: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=74b4f847-6b13-45b6-9a95-fa2cd405c4a6',
  cookies: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=b68d815d-0463-455f-8bb1-1aef0ad6efd2',
} as const;



interface Props {
  title: string;
  // Canonical Termly-hosted policy for this page (real existing content).
  // OPTIONAL: SAB-722's /withdrawals page is authored natively in-repo from
  // verified processor answers and our own code — there is no Termly document
  // behind it, so it renders children only and skips the viewer section rather
  // than embedding an empty iframe.
  termlyUrl?: string;
  children?: ReactNode;
}

export default function LegalPageShell({ title, termlyUrl, children }: Props) {
  return (
    <MarketingLayout>
      <header>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      </header>

      {/* Page-specific required sections, if any. SAB-767 removed the Aeropay one. */}
      {children}

      {termlyUrl && (
      <section className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Current published {title}. If the embedded viewer does not load, open
          it directly:
        </p>
        <a
          href={termlyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-sm font-medium text-primary underline underline-offset-4 hover:text-primary/80"
        >
          View the full {title} &rarr;
        </a>
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <iframe
            src={termlyUrl}
            title={title}
            loading="lazy"
            className="w-full"
            style={{ height: '70vh', border: 0 }}
          />
        </div>
      </section>
      )}
    </MarketingLayout>
  );
}
